"use client";

import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const galleryItems = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1561763/img/elegant_mosquito_screen_modern_window.png",
  category: "Serramenti",
  title: "Luminosità e Comfort",
  alt: "Ampia vetrata scorrevole in soggiorno moderno"
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1561763/img/minimalist_sliding_door_patio.png",
  category: "Porte Scorrevoli",
  title: "Continuità Interno-Esterno",
  alt: "Porta scorrevole minimalista che affaccia sul patio"
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1561763/img/elegant_wooden_staircase_detail.png",
  category: "Scale",
  title: "Design Artigianale",
  alt: "Dettaglio di scala in legno di design moderno"
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1561763/img/bioclimatic_pergola_garden_sunset.png",
  category: "Pergole",
  title: "Vivere l'Outdoor",
  alt: "Pergola bioclimatica in giardino al tramonto"
}, {
  id: 5,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1561763/img/security_grille_ultra_hd.png",
  category: "Sicurezza",
  title: "Protezione con Stile",
  alt: "Close-up railings of a villa window"
}, {
  id: 6,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1561763/img/white_aluminum_shutters_facade.png",
  category: "Oscuranti",
  title: "Persiane in Alluminio",
  alt: "Persiane bianche in alluminio su casa ristrutturata"
}, {
  id: 7,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1561763/img/glass_veranda_winter_garden.png",
  category: "Verande",
  title: "Giardino d'Inverno",
  alt: "Veranda in vetro chiusa per uso invernale"
}, {
  id: 8,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1561763/img/entrance_door_security_design.png",
  category: "Portoncini",
  title: "Ingresso di Prestigio",
  alt: "Portoncino d'ingresso blindato di design contemporaneo"
}, {
  id: 9,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1561763/img/luxury_awning_terrace_view.png",
  category: "Tende da Sole",
  title: "Eleganza Estiva",
  alt: "Tenda da sole moderna su un terrazzo panoramico"
}, {
  id: 10,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1561763/img/elegant_mosquito_screen_modern_window.png",
  category: "Zanzariere",
  title: "Protezione Invisibile",
  alt: "Zanzariera di design in un ambiente domestico luminoso e raffinato"
}, {
  id: 11,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1561763/img/modern_glass_canopy_entrance.png",
  category: "Pensiline",
  title: "Design Funzionale",
  alt: "Pensilina in vetro e acciaio sopra un ingresso moderno"
}, {
  id: 12,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1561763/img/automated_security_shutter_home.png",
  category: "Tapparelle",
  title: "Efficienza e Sicurezza",
  alt: "Tapparella blindata automatizzata su una finestra di design"
}];
export default function IdeeESoluzioni() {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0,
    rootMargin: '0px 0px -10% 0px'
  });
  const handleNext = useCallback(() => {
    setSelectedIndex(prev => prev === null ? null : (prev + 1) % galleryItems.length);
  }, []);
  const handlePrev = useCallback(() => {
    setSelectedIndex(prev => prev === null ? null : (prev - 1 + galleryItems.length) % galleryItems.length);
  }, []);
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (selectedIndex === null) return;
    if (e.key === "ArrowRight") handleNext();
    if (e.key === "ArrowLeft") handlePrev();
    if (e.key === "Escape") setSelectedIndex(null);
  }, [selectedIndex, handleNext, handlePrev]);
  useEffect(() => {
    if (selectedIndex !== null) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedIndex, handleKeyDown]);
  const selectedItem = selectedIndex !== null ? galleryItems[selectedIndex] : null;
  return <section id="idee-e-soluzioni" ref={ref} className="bg-background py-24 w-full">
      <div className="max-w-7xl mx-auto px-8">
        <div className={`text-center mb-16 transition-all duration-700 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4 tracking-tight">
            Idee e Soluzioni
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Esplora la galleria delle nostre realizzazioni.<br />Ogni progetto racconta una storia di sicurezza, comfort e design su misura.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {galleryItems.map((item, index) => <div key={item.id} data-index={index} className={`group relative cursor-pointer rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-500 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
          transitionDelay: `${index * 100}ms`
        }} onClick={() => setSelectedIndex(index)}>
              <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
                <img src={item.src} alt={item.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
              </div>

              <div className="absolute inset-0 bg-primary/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center p-4 text-center backdrop-blur-[2px]">
                <ZoomIn className="text-white w-8 h-8 mb-2 opacity-80" />
                <span className="text-white font-semibold text-lg tracking-wide">
                  {item.category}
                </span>
                <span className="text-white/80 text-sm mt-1">
                  {item.title}
                </span>
              </div>
            </div>)}
        </div>
      </div>

      <Dialog open={selectedIndex !== null} onOpenChange={open => !open && setSelectedIndex(null)}>
        <DialogContent showCloseButton={false} className="max-w-[95vw] max-h-[95vh] w-full h-full bg-transparent border-none shadow-none p-0 flex flex-col items-center justify-center outline-none">
          <DialogTitle className="sr-only">
            {selectedItem?.title || "Dettaglio immagine"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {selectedItem?.alt || "Visualizzazione ingrandita del progetto"}
          </DialogDescription>

          {selectedItem && <div className="relative w-full h-full flex items-center justify-center">
              <Button variant="ghost" size="icon" className="absolute top-4 right-4 z-50 text-white hover:bg-white/20 rounded-full w-12 h-12" onClick={() => setSelectedIndex(null)} aria-label="Chiudi galleria">
                <X className="w-6 h-6" />
              </Button>

              <Button variant="ghost" size="icon" className="absolute left-4 z-50 text-white hover:bg-white/20 rounded-full w-12 h-12 hidden md:flex" onClick={e => {
            e.stopPropagation();
            handlePrev();
          }} aria-label="Immagine precedente">
                <ChevronLeft className="w-8 h-8" />
              </Button>

              <Button variant="ghost" size="icon" className="absolute right-4 z-50 text-white hover:bg-white/20 rounded-full w-12 h-12 hidden md:flex" onClick={e => {
            e.stopPropagation();
            handleNext();
          }} aria-label="Immagine successiva">
                <ChevronRight className="w-8 h-8" />
              </Button>

              <div className="relative max-w-full max-h-[85vh] overflow-hidden rounded-lg shadow-2xl" onClick={e => e.stopPropagation()}>
                <img src={selectedItem.src} alt={selectedItem.alt} className="max-w-full max-h-[85vh] object-contain w-auto h-auto" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 pt-12 text-white">
                  <h3 className="text-xl font-semibold">
                    {selectedItem.title}
                  </h3>
                  <p className="text-white/80 text-sm mt-1">
                    {selectedItem.category}
                  </p>
                </div>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
    </section>;
}